import { useState, useEffect } from 'react'

import { ErrorBoundary } from "react-error-boundary"
import { ConfirmProvider } from "material-ui-confirm"
import { SnackbarProvider, closeSnackbar, enqueueSnackbar } from 'notistack'
import cookie from 'react-cookies'
import _ from "lodash"

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { grey, green, teal, blueGrey, lime, amber } from '@mui/material/colors'
import CloseIcon from '@mui/icons-material/Close'

import { checkLoggedIn } from '/providers/auth-api'
import { getClientDetails } from './providers/clients-api'
import { saveUserPreferences } from '/providers/studio-api'
import { getClientTemplates, getClientTemplate, getClientImages } from '/providers/templates-api'
import { getPass } from '/providers/passes-api'

import { AppContext } from "/providers/app-context"
import AppRoutes from './AppRoutes'
import Error from '/pages/error'

const theme = createTheme({
  typography: {
    // Force all typography variants to use px
    pxToRem: (px) => `${Math.round(px)}px`
  },
  colorSchemes: {
    light: {
      palette: {
        type: "light",
        primary: {
          main: teal[700],
          light: teal[200],
        },
        menubar: {
          bg: teal[100],
          fg: "#303030",
          crumbs: grey[400]
        },
        table: {
          row: blueGrey[700],
          head: blueGrey[600],
        },
        paper: grey[200],
        secondary: {
          main: green[300]
        },
        lightgray: blueGrey[400],
        darkgray: blueGrey[600],
      },
    },
    dark: {
      palette: {
        type: "dark",
        primary: {
          main: amber[200],
          light: amber[100]
        },
        secondary: {
          main: lime[300]
        },
        table: {
          row: '#1E1E1E',
          head: '#404040',
        },
        paper: "#fff",
        menubar: {
          bg: '#1E1E1E',
          fg: '#f4f4f6',
          crumbs: grey[600]
        },
        lightgray: blueGrey[300],
        darkgray: blueGrey[600],
      },
    },
  },
  components: {
      MuiCssBaseline: {
        styleOverrides: theme => ({
          body: {
            background: theme.palette.mode === 'dark'
              ? `
            radial-gradient(black 15%, transparent 16%) 0 0,
            radial-gradient(black 15%, transparent 16%) 8px 8px,
            radial-gradient(rgba(255, 255, 255, .1) 15%, transparent 20%) 0 1px,
            radial-gradient(rgba(255, 255, 255, .1) 15%, transparent 20%) 8px 9px
          `
              : `
            radial-gradient(#e4e4e6 15%, transparent 16%) 0 0,
            radial-gradient(#e4e4e6 15%, transparent 16%) 8px 8px,
            radial-gradient(rgba(255, 255, 255, .1) 15%, transparent 20%) 0 1px,
            radial-gradient(rgba(255, 255, 255, .1) 15%, transparent 20%) 8px 9px
          `,
            backgroundColor: theme.palette.mode === 'dark' ? '#282828' : '#f4f4f6',
            backgroundSize: '16px 16px',
          },
          '.dashboard h1, .dashboard h2, .dashboard h3, .dashboard h4, .dashboard h5, .dashboard h6': {
            color: theme.palette.primary.main,
          },
        }),
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            // Outlined TextField 
            "& .MuiOutlinedInput-root": {
              fontFamily: "Arial",
              fontWeight: "bold",
              "& .MuiOutlinedInput-notchedOutline": {
                borderWidth: "1px",
              },
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "3px",
                },
              },
              "&.Mui-disabled": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px",
                },
              },
              "&:hover:not(.Mui-focused,.Mui-disabled)": {
                "& .MuiOutlinedInput-notchedOutline": {
                },
              },
            },
            "& .MuiInputLabel-outlined": {
              fontWeight: "bold",
              "&.Mui-focused": {
                color: "secondary.main",
              },
            },
          },
        },
      },
    },
  })


const checkLoginToken = async ({ apiSource, setLoading, setApiKeys, setClient, setUser }) => {
  try {
    let loginResponse = await checkLoggedIn({ apiSource })

    if (loginResponse.authenticated) {
      setApiKeys(loginResponse.keys)
      setClient(loginResponse.client)
      setUser(loginResponse.user)
    }

    setLoading(false)
  }
  catch (err) {
    return null
  }
}

const checkApiSourceCookie = () => {
  if (!["localhost", "127.0.0.1", "local.smartix.uk"].includes(window.location.hostname)) return "remote" // Always remote, regardless of cookie when using deployed app

  let s = cookie.load("apiSource")
  if (!s) {
    s = "remote"
    setApiSourceCookie(s)
  }

  return s
}

const setApiSourceCookie = source => {
  cookie.save('apiSource', source, { path: '/' })
}

const App = () => {
  const [loading, setLoading] = useState(true)

  const [apiSource, setApiSource] = useState(checkApiSourceCookie())
  const [apiKeys, setApiKeys] = useState({})
  const [user, setUser] = useState(null)
  const [client, setClient] = useState(null)
  const [templates, setTemplates] = useState([])
  const [passes, setPasses] = useState([])
  const [processing, setProcessing] = useState(false)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [imageList, setImageList] = useState([])
  const [speeddialVisible, setSpeeddialVisible] = useState(false)
  const [copyingEmail, setCopyingEmail] = useState(false)
  const [emailBarcodes, setEmailBarcodes] = useState(false)
  const [giftCard, setGiftCard] = useState(null)

  const saveUser = async u => {
    setUser(u)
    await saveUserPreferences({ apiSource, apiKeys, user: u })
  }

  const refreshTemplates = async () => {
    setLoadingTemplates(true)
    const tt = await getClientTemplates({ apiSource, apiKeys })

    if (!tt.error) {
      setTemplates(tt)
      setLoadingTemplates(false)
    }
    //TODO handle error
  }

  const refreshFileList = async () => {
    const result = await getClientImages({ apiSource, apiKeys })
    //todo handler error
    if (result.ok) {
      setImageList(result.images)
    } else {
      enqueueSnackbar({ message: result.error, variant: "error" })
    }
  }

  const refreshTemplate = async templateId => {
    setLoadingTemplates(true)
    const reloadedTemplate = await getClientTemplate({ apiSource, apiKeys, templateId })

    if (!reloadedTemplate.error) {
      const tt = _.cloneDeep(templates)
      const index = _.findIndex(tt, i => i.id === templateId)
      tt[index] = reloadedTemplate

      setTemplates(tt)
    }
    // TODO handle error
    setLoadingTemplates(false)
  }

  const refreshClient = async () => {
    const c = await getClientDetails({ apiSource, apiKeys })
    if (c.ok) setClient(c.client)
    // TODO Add error handling
  }

  const refreshPass = async ({ templateId, passId }) => {
    const reloaded_pass = await getPass({ apiSource, apiKeys, templateId, passId })

    const pp = _.cloneDeep(passes)
    const index = _.findIndex(pp, i => i.template_id === templateId && i.pass_id === passId)
    if (index !== -1) pp[index] = reloaded_pass

    setPasses(pp)
  }

  useEffect(() => {
    checkLoginToken({ apiSource, setLoading, setApiKeys, setClient, setUser, setApiSource })
  }, [])

  useEffect(() => {
    if (!loading) setApiSourceCookie(apiSource)
  }, [apiSource])

  useEffect(() => {
    if (!loading && client) {
      refreshTemplates()
      refreshFileList()
    }
  }, [client])

  return (
    <ErrorBoundary FallbackComponent={Error}>
      <ThemeProvider theme={theme} noSsr>
        <CssBaseline enableColorScheme />
        <AppContext.Provider value={{ apiSource, setApiSource, apiKeys, setApiKeys, copyingEmail, setCopyingEmail, emailBarcodes, setEmailBarcodes, giftCard, setGiftCard, loadingTemplates, processing, setProcessing, speeddialVisible, setSpeeddialVisible, showHelp, setShowHelp, user, setUser, saveUser, client, setClient, refreshClient, templates, refreshTemplate, refreshTemplates, passes, setPasses, refreshPass, imageList, setImageList }}>
          <ConfirmProvider defaultOptions={{ confirmationButtonProps: { autoFocus: true } }}>
            <SnackbarProvider
              autoHideDuration={3000}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              action={(snackbarId) => (
                <button onClick={() => closeSnackbar(snackbarId)}>
                  <CloseIcon />
                </button>
              )}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <AppRoutes client={client} loading={loading} />
              </LocalizationProvider>
            </SnackbarProvider>
          </ConfirmProvider>
        </AppContext.Provider>
      </ThemeProvider>
    </ErrorBoundary >
  )
}

export default App
